import { Pagination, TableRow } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import { useGetTransactionsQuery } from '@/api/v1';
import { Clickable } from '@/components/atoms/Clickable';
import { TableCellErrorBoundary } from '@/components/atoms/TableCellErrorBoundary';
import { TableContentSelector } from '@/components/atoms/TableContentSelector';
import { TableRowErrorBoundary } from '@/components/atoms/TableRowErrorBoundary';
import { HeaderCell } from '@/components/molecules/HeaderCell';
import { TransactionTableCell } from '@/components/molecules/TransactionTableCell';
import { useTranslator } from '@/hooks/useTranslator';
import { createHeaders } from '@/Screens/Dashboard/Activity/headers';
import { GetTransactionsFilters } from '@/types/getTransactionsFilters';
import { HeaderItem } from '@/types/headerItem';
import { Page } from '@/types/page';
import { Transaction } from '@/types/transaction';
import { TransactionColumn } from '@/types/transactionColumn';
import { TransactionRow } from '@/types/transactionRow';
import { countTransactionFilters } from '@/utility/countTransactionFilters';
import { computeSortingValue, parseSort } from '@/utility/sorting';

interface Props {
  readonly beneficiaryId?: string | null;
  readonly filters: Partial<GetTransactionsFilters>;
  readonly onFiltersChange: (filters: GetTransactionsFilters) => void;
  readonly onRowClick: (transactionId: string) => void;
  readonly noContentView: React.ReactElement;
  readonly noResultsView: React.ReactElement;
  readonly visibleColumns: readonly TransactionColumn[];
  readonly contentFilter?: (transaction: Transaction) => boolean;
}

export const TransactionsTable: React.FC<Props> = (props: Props): React.ReactElement => {
  const {
    beneficiaryId,
    filters,
    onFiltersChange,
    onRowClick,
    noContentView,
    noResultsView,
    visibleColumns,
    contentFilter,
  } = props;

  const tr = useTranslator();

  const queryFilters = React.useMemo(
    (): Partial<GetTransactionsFilters> => ({
      ...filters,
      beneficiaryId: filters.beneficiaryId || beneficiaryId || undefined,
    }),
    [filters, beneficiaryId],
  );

  // Skip only when beneficiaryId is undefined (waiting for data), not when null (explicit "show all")
  const isSkipped = beneficiaryId === undefined;

  const { data: transactionsPage = Page.empty(), status: queryStatus } = useGetTransactionsQuery(
    queryFilters,
    {
      skip: isSkipped,
    },
  );

  // When query is skipped, treat it as fulfilled with empty data to show the noContentView
  const fetchingStatus = isSkipped ? QueryStatus.fulfilled : queryStatus;

  const sorting = React.useMemo((): Record<string, 'asc' | 'desc'> => {
    const sort = filters.sort ?? [];
    if (sort.length > 0) {
      if (typeof sort === 'string') {
        return parseSort([sort]);
      } else {
        return parseSort(sort);
      }
    }

    return {
      createdDate: 'desc',
      type: 'desc',
      amount: 'desc',
    };
  }, [filters.sort]);

  const {
    content: transactions,
    totalPages,
    totalElements,
    pageable: { pageNumber },
  } = transactionsPage;

  const handleSort = React.useCallback(
    (column: string): void => {
      const currentFilters = filters as GetTransactionsFilters;
      const sort =
        typeof currentFilters.sort === 'string'
          ? [currentFilters.sort]
          : (currentFilters.sort ?? []);

      const current = sort
        .map((item: string): [string, string] => {
          const [targetColumn, direction] = item.split(',');
          return [targetColumn, direction];
        })
        .filter(
          (item: [string, string]): item is [string, 'asc' | 'desc'] =>
            item[1] === 'asc' || item[1] === 'desc',
        )
        .find(([targetColumn]: [string, 'asc' | 'desc']): boolean => {
          return targetColumn === column;
        }, []);

      if (!current) {
        onFiltersChange({
          ...currentFilters,
          sort: [`${column},asc`, ...sort],
          page: 0,
        });
        return;
      }
      const [, direction] = current;
      const computedDirection = computeSortingValue(direction);

      onFiltersChange({
        ...currentFilters,
        sort: [
          `${column},${computedDirection}`,
          ...sort.filter((item: string): boolean => item.split(',')?.[0] !== column),
        ],
        page: 0,
      });
    },
    [filters, onFiltersChange],
  );

  const headers = React.useMemo(
    (): readonly HeaderItem<TransactionRow>[] => createHeaders(tr, visibleColumns),
    [tr, visibleColumns],
  );

  const { widths, totalWidth } = React.useMemo(
    (): { widths: readonly number[]; totalWidth: number } => getColumnWidths(visibleColumns),
    [visibleColumns],
  );

  const rows = React.useMemo(
    (): readonly TransactionRow[] =>
      contentFilter ? transactions.filter(contentFilter) : transactions,
    [transactions, contentFilter],
  );

  const handleRowClick = React.useCallback(
    (transactionId: string): void => {
      onRowClick(transactionId);
    },
    [onRowClick],
  );

  const onPageChange = React.useCallback(
    (_: React.ChangeEvent<unknown>, page: number): void => {
      onFiltersChange({
        ...(filters as GetTransactionsFilters),
        page: page,
      });
    },
    [filters, onFiltersChange],
  );

  const filtersCount = React.useMemo((): number => countTransactionFilters(filters), [filters]);

  return (
    <>
      <Paper elevation={3} sx={styles.paper}>
        <TableContentSelector
          status={fetchingStatus}
          noContentView={noContentView}
          noResultsView={noResultsView}
          filtersCount={filtersCount}
          data={rows}>
          <Table sx={styles.table}>
            <colgroup>
              {widths.map(
                (width: number, index: number): React.ReactElement => (
                  <col key={index} width={width / totalWidth} />
                ),
              )}
            </colgroup>
            <TableHead>
              <TableRow>
                {headers.map(
                  (item: HeaderItem<TransactionRow>): React.ReactElement => (
                    <HeaderCell key={item.key} item={item} sorting={sorting} onClick={handleSort} />
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row: TransactionRow): React.ReactElement => {
                return (
                  <TableRowErrorBoundary
                    key={row.id}
                    columnCount={headers.length}
                    fallbackMessage="Unable to display transaction data">
                    <Clickable data={row.id} onClick={handleRowClick}>
                      <TableRow sx={styles.row} hover={true}>
                        {headers.map(
                          (header: HeaderItem<TransactionRow>): React.ReactElement => (
                            <TableCellErrorBoundary
                              key={header.key}
                              sx={styles.cell}
                              fallbackMessage="N/A">
                              <TransactionTableCell
                                transaction={row}
                                field={header.key}
                                sx={styles.cell}
                              />
                            </TableCellErrorBoundary>
                          ),
                        )}
                      </TableRow>
                    </Clickable>
                  </TableRowErrorBoundary>
                );
              })}
            </TableBody>
          </Table>
        </TableContentSelector>
      </Paper>
      {totalElements > 0 && rows.length > 0 && (
        <Box display="flex" alignItems="center" justifyContent="center">
          <Pagination count={totalPages} page={pageNumber + 1} onChange={onPageChange} />
        </Box>
      )}
    </>
  );
};

const styles: Record<string, SystemStyleObject> = {
  paper: {
    flex: 1,
  },
  loadingContainer: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
  },
  cell: {
    minWidth: 0,
    '&:first-of-type': {
      pl: 5,
    },
    '&:last-of-type': {
      pr: 5,
    },
  },
  cellContent: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  row: {
    cursor: 'pointer',
  },
};

/** Column widths mapping */
const columnWidths: Record<TransactionColumn, number> = {
  [TransactionColumn.DATE]: 136,
  [TransactionColumn.TYPE]: 99,
  [TransactionColumn.METHOD]: 90,
  [TransactionColumn.AMOUNT]: 200,
  [TransactionColumn.FROM]: 116,
  [TransactionColumn.TO]: 116,
  [TransactionColumn.STATUS]: 155,
  [TransactionColumn.MARKUP]: 100,
  [TransactionColumn.EARNED]: 130,
};

const getColumnWidths = (
  visibleColumns: readonly TransactionColumn[],
): { widths: readonly number[]; totalWidth: number } => {
  const widths = visibleColumns.map((column: TransactionColumn): number => columnWidths[column]);
  const totalWidth = widths.reduce((total: number, width: number): number => total + width, 0);
  return { widths, totalWidth };
};
